import { useRouter } from 'next/router';
import React from 'react';

import { useServiceRequestsCount } from '@commenda/api';
import { useLocalStorage, useToggle } from '@commenda/kakashi';
import { classNames, cn } from '@commenda/utils';

import { ServiceRequestStatus, ServiceSubcategory } from '@commenda/prisma';
import { useConfig } from 'context/useConfig';
import { useCommendaNavigation } from 'hooks/useCommendaNavigation';

import { ChatPanelAside } from 'components/chat-panel/ChatPanelAside';
import { ChatPanelLauncher } from 'components/chat-panel/ChatPanelLauncher';
import { ChatPanelHelper } from 'components/chat-panel/helpers/chat-panel-helper';
import { useChatPanel } from 'context/useChatPanel';

import { CommendaLayoutAside } from './CommendaLayoutAside';
import { CommendaNavbar } from './CommendaNavbar';
import { HelpAndSupportModal } from './HelpAndSupportModal';
import { ProductChangelogWidget } from './ProductChangelogWidget';
import { ReferralAnnouncementModal } from './ReferralAnnouncementModal';

interface CommendaLayoutAsideProp {
  content: React.ReactNode;
  variant: 'border' | 'detached' | 'plain';
  width?: string;
  className?: string;
}

interface CommendaLayoutProps {
  children: React.ReactNode;
  className?: string;
  breadcrumb?: React.ReactNode;
  aside?: CommendaLayoutAsideProp;
  disableContentPadding?: boolean;
  /**
   * Optional content rendered as a horizontal strip above the white card,
   * inside CommendaLayout's outer padding (so it sits on the gray page bg,
   * not inside the card). Spans full width.
   */
  topBarContent?: React.ReactNode;
  /**
   * Full-bleed mode: drops the framed white card and the gradient backdrop so
   * children fill the inset edge-to-edge. Used by the agent chat surface.
   */
  bare?: boolean;
  contentBackground?: 'primary' | 'light';
}

const CommendaLayout = ({
  children,
  className,
  breadcrumb,
  aside,
  disableContentPadding = false,
  topBarContent,
  bare = false,
  contentBackground = 'primary',
}: CommendaLayoutProps) => {
  const [isSidebarExpanded] = useLocalStorage('is-sidebar-expanded', true);

  const router = useRouter();
  const { companyId } = useConfig();

  const { count: onboardingServiceRequestsCount } = useServiceRequestsCount({
    companyId: companyId ?? undefined,
    serviceSubcategories: [ServiceSubcategory.TNA_ONBOARDING],
    includedStatuses: [
      ServiceRequestStatus.NOT_STARTED,
      ServiceRequestStatus.IN_PROGRESS,
    ],
    enabled: !!companyId,
  });

  const hasActiveOnboardingRequests = onboardingServiceRequestsCount > 0;

  const isHelpAndSupportModalOpen = useToggle(false);

  const {
    navGroups,
    sidebarUser,
    navMode,
    onModeChange,
    recents,
    chatNavItems,
    agentSessions,
    productChangelog,
    referralAnnouncement,
  } = useCommendaNavigation({
    onHelpAndSupportClick: isHelpAndSupportModalOpen.on,
  });

  const chatPanel = useChatPanel();
  const isChatOpen = chatPanel.isOpen;

  // One resolution point for the whole layout. When the chat is open it owns the column and the
  // page's own aside stands down; closing it hands the column straight back. This is what delivers
  // the swap for every aside-owning page without editing any of them.
  const resolvedAside: CommendaLayoutAsideProp | undefined = isChatOpen
    ? {
        content: <ChatPanelAside />,
        variant: 'plain',
        width: `${ChatPanelHelper.PANEL_WIDTH_PX}px`,
        className: cn('overflow-hidden', ChatPanelHelper.VIEW_TRANSITION_CLASS),
      }
    : aside;

  // Both `detached` and `plain` place the aside beside the content card rather than inside it; they
  // differ only in whether it carries a card treatment.
  const isAsideOutsideCard =
    resolvedAside?.variant === 'detached' || resolvedAside?.variant === 'plain';

  const asideElement = resolvedAside ? (
    <CommendaLayoutAside
      variant={resolvedAside.variant}
      width={resolvedAside.width}
      className={resolvedAside.className}
    >
      {resolvedAside.content}
    </CommendaLayoutAside>
  ) : null;

  // `children` keeps one position in the tree -- outer row > card > inner row > scroll div -- in
  // every combination of aside placement and breadcrumb. Only classes change, so toggling the panel
  // never remounts the page and its scroll position and local state survive.
  const contentCard = (
    <div
      // The chat panel's page-context harvester reads exactly this subtree: the page's content,
      // never the nav, the chat aside, or the launcher.
      data-ai-main=""
      className={cn(
        'flex-1 rounded-xl overflow-hidden shadow-xs border border-border-primary flex flex-col',
        contentBackground === 'light' ? 'bg-bg-light' : 'bg-bg-primary',
        !isAsideOutsideCard && 'w-full'
      )}
    >
      {breadcrumb}
      <div className="flex flex-1 overflow-hidden">
        <div
          className={classNames(
            breadcrumb
              ? 'flex flex-col gap-4xl flex-1 overflow-y-auto overflow-x-auto'
              : cn(
                  'rounded-lg overflow-y-auto flex-1 overflow-x-auto',
                  !disableContentPadding && 'p-3xl'
                ),
            className
          )}
        >
          {children}
        </div>
        {!isAsideOutsideCard && asideElement}
      </div>
    </div>
  );

  return (
    <main className="relative flex h-screen bg-bg-light overflow-hidden">
      {/* The chat panel sits on the page's own fill, so the gradient would tint the strip between
       * it and the content card. It stands down while the panel is open. */}
      {!bare && !isChatOpen && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src="/background-gradient.svg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none h-screen absolute top-0 bottom-0 right-0 z-[1] object-cover object-left transition-[left] duration-200 ease-linear"
          style={{
            left: isSidebarExpanded ? '144px' : '0px',
          }}
        />
      )}
      {productChangelog && (
        <ProductChangelogWidget
          isOpen={productChangelog.isOpen}
          onOpenChange={productChangelog.onOpenChange}
        />
      )}
      {referralAnnouncement.isOpen && (
        <ReferralAnnouncementModal
          onClose={() => referralAnnouncement.onOpenChange(false)}
        />
      )}
      <CommendaNavbar
        groups={navGroups}
        user={sidebarUser}
        mode={navMode}
        onModeChange={onModeChange}
        recents={recents}
        chatNavItems={chatNavItems}
        agentSessions={agentSessions}
        onboardingPill={
          hasActiveOnboardingRequests
            ? {
                isActive: router.pathname.startsWith('/onboarding'),
                onClick: () => router.push('/onboarding'),
              }
            : undefined
        }
      >
        {bare ? (
          <div className="relative z-[2] flex h-screen w-full flex-col overflow-hidden">
            {children}
          </div>
        ) : (
          <div className="relative z-[2] flex flex-col items-center h-screen pt-md pr-md pb-md gap-md">
            {topBarContent && (
              <div className="w-full shrink-0">{topBarContent}</div>
            )}
            <div
              className={cn(
                'flex flex-1 w-full overflow-hidden',
                isAsideOutsideCard && 'gap-md'
              )}
            >
              {contentCard}
              {isAsideOutsideCard && asideElement}
            </div>
            {/* Only where the aside can render: a bare layout has no column for the panel, so a
                launcher there would toggle something invisible. */}
            <ChatPanelLauncher />
          </div>
        )}
      </CommendaNavbar>
      <HelpAndSupportModal
        isOpen={isHelpAndSupportModalOpen.value}
        onClose={isHelpAndSupportModalOpen.off}
      />
    </main>
  );
};

export { CommendaLayout };
export type { CommendaLayoutAsideProp, CommendaLayoutProps };
